import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { GridLogicOperator } from "@mui/x-data-grid-pro"

import { AsyncRequestService } from "src/api"
import { adminv3_AsyncRequestResponse } from "src/api/models/adminv3_AsyncRequestResponse"
import { adminv3_AsyncRequestStatusSummaryResponse } from "src/api/models/adminv3_AsyncRequestStatusSummaryResponse"
import { avy_api_pkg_async_AsyncStatus } from "src/api/models/avy_api_pkg_async_AsyncStatus"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { parseDate } from "src/lib/date"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportUnhandledApiError } from "src/lib/report"

import { IAdvanceQueryModel, IColumn } from "src/types/data-grid-pro"
import { IDateRangeValue } from "src/types/date_range_picker"
import {
    IPostInitialStateFilterItem,
    IRouteParams,
} from "src/views/community-posts-and-comments/types"
import {
    getOperatorForField,
    handleDateFilter,
    parseFieldValue,
} from "src/helpers/filterHelpers"

export interface IAsyncRequestListItem {
    id: number
    request_id: string
    status: avy_api_pkg_async_AsyncStatus
    endpoint: string
    method: string
    created_at?: Date | null
    started_at?: Date | null
    completed_at?: Date | null
    total_items: number
    completed_items: number
    failed_items: number
    pending_items: number
    integration_id?: number
    user_id?: number
    error_summary?: string
}

export class AsyncRequestsStore implements IDisposable {
    static Context = React.createContext<AsyncRequestsStore | null>(null)
    static LoadingKeys = createLoadingKeys("pageLoad", "statusSummary")

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    statusSummary: adminv3_AsyncRequestStatusSummaryResponse[] = []
    dateRangeFilter: IDateRangeValue = { from: null, to: null }

    private repositoryUpdatesListenerDisposer?: () => void

    asyncRequests = new Pagination(
        async (params) => {
            const response =
                await AsyncRequestService.postV3AdminAsyncRequestList({
                    request: {
                        page_number: params.page,
                        page_size: params.pageSize,
                        filter: params.advanceQuery,
                    },
                })

            const items: IAsyncRequestListItem[] =
                response.items?.map(
                    (asyncRequest: adminv3_AsyncRequestResponse) => ({
                        id: asyncRequest.id ?? -1,
                        request_id: asyncRequest.request_id ?? "",
                        status: asyncRequest.status ?? "created",
                        endpoint: asyncRequest.endpoint ?? "",
                        method: asyncRequest.method ?? "",
                        created_at: parseDate(asyncRequest.created_at),
                        started_at: parseDate(asyncRequest.started_at),
                        completed_at: parseDate(asyncRequest.completed_at),
                        total_items: asyncRequest.total_items ?? 0,
                        completed_items: asyncRequest.completed_items ?? 0,
                        failed_items: asyncRequest.failed_items ?? 0,
                        pending_items: asyncRequest.pending_items ?? 0,
                        integration_id: asyncRequest.integration_id,
                        user_id: asyncRequest.user_id,
                        error_summary: asyncRequest.error_summary,
                    }),
                ) ?? []

            return {
                items,
                rawItems: response.items ?? [],
                count: response.total_count ?? 0,
            }
        },
        { loadingKey: AsyncRequestsStore.LoadingKeys.pageLoad },
    )

    constructor() {
        makeAutoObservable(this)
    }

    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.setAccessGroupId(accessGroupId)

        // Listen to repository updates for real-time updates
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "async-request"
                ) {
                    await this.asyncRequests.reload()
                }
            },
        )

        await Promise.all([
            this.asyncRequests.loadInitialPage(advanceQuery),
            this.loadStatusSummary(),
        ])
    }

    async loadStatusSummary() {
        try {
            this.statusSummary =
                await AsyncRequestService.getV3AdminAsyncRequestStatusSummary({
                    dateFrom: this.dateRangeFilter.from?.toISOString(),
                    dateTo: this.dateRangeFilter.to?.toISOString(),
                })
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }

    getStatusChipState(
        status: avy_api_pkg_async_AsyncStatus,
    ): "positive" | "neutral" | "negative" | "info" | "other" | "dark" {
        switch (status) {
            case "completed":
                return "positive"
            case "failed":
                return "negative"
            case "processing":
                return "neutral"
            case "partial":
                return "neutral"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "other"
        }
    }

    getStatusColor(
        status: avy_api_pkg_async_AsyncStatus,
    ):
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning" {
        switch (status) {
            case "completed":
                return "success"
            case "failed":
                return "error"
            case "processing":
                return "warning"
            case "partial":
                return "warning"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "default"
        }
    }

    getStatusText(status: avy_api_pkg_async_AsyncStatus): string {
        switch (status) {
            case "created":
                return t`async-requests.status.created`
            case "pending":
                return t`async-requests.status.pending`
            case "pending_send":
                return t`async-requests.status.pending-send`
            case "sent":
                return t`async-requests.status.sent`
            case "processing":
                return t`async-requests.status.processing`
            case "completed":
                return t`async-requests.status.completed`
            case "failed":
                return t`async-requests.status.failed`
            case "partial":
                return t`async-requests.status.partial`
            default:
                return status
        }
    }

    private setAccessGroupId(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private getAccessGroupId() {
        return this._accessGroupId
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.asyncRequests.loadAdvanceQuery(advanceQuery)
    }

    setDateRangeFilter(dateRange: IDateRangeValue) {
        this.dateRangeFilter = dateRange
    }

    async applyDateRangeFilter(dateRange: IDateRangeValue) {
        this.setDateRangeFilter(dateRange)
        await this.loadStatusSummary()
    }

    // Override the advance query to include date filtering
    getAdvanceQueryWithDateFilter(
        baseQuery?: IAdvanceQueryModel,
    ): IAdvanceQueryModel | undefined {
        if (
            this.dateRangeFilter.from == null &&
            this.dateRangeFilter.to == null &&
            baseQuery == null
        ) {
            return undefined
        }

        const dateFilter = []

        if (this.dateRangeFilter.from != null) {
            dateFilter.push({
                field: "created_at",
                operator: "onOrAfter",
                value: this.dateRangeFilter.from.toISOString(),
            })
        }

        if (this.dateRangeFilter.to != null) {
            dateFilter.push({
                field: "created_at",
                operator: "onOrBefore",
                value: this.dateRangeFilter.to.toISOString(),
            })
        }

        const existingItems = baseQuery?.filter?.items ?? []

        return {
            filter: {
                items: [...existingItems, ...dateFilter],
                logicOperator: GridLogicOperator.And,
            },
            sort: baseQuery?.sort,
        }
    }

    handleParams(
        columnHeaders: IColumn<IAsyncRequestListItem>[],
        params: IRouteParams[] = [],
    ): IPostInitialStateFilterItem[] {
        const filterableColumns = columnHeaders.filter(
            (column) =>
                column.filterable !== false && column.type !== undefined,
        )

        return params.flatMap((param) => {
            const column = filterableColumns.find(
                (col) => col.field === param.param,
            )
            if (column == null) return []

            const { field, type } = column

            try {
                if (
                    type === "dateTime" &&
                    (field === "created_at" || field === "completed_at")
                ) {
                    return handleDateFilter(param.value, field)
                }

                const value = parseFieldValue(field, param.value)
                const operator = getOperatorForField(field, type, value)

                return [
                    {
                        field: param.param,
                        operator,
                        value,
                    },
                ]
            } catch (error) {
                return []
            }
        })
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }
}
